import React, { useState, useEffect } from 'react';
import { 
  User, 
  Mail, 
  Phone, 
  Camera,
  Save,
  Edit,
  X,
  Shield,
  Calendar,
  MapPin
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const AdminProfile = () => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);


  // Lấy thông tin từ AuthContext
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    avatar: user?.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
    role: user?.role || '',
    joinDate: user?.createdAt || '',
    address: user?.address || '',
    birthDate: user?.birthDate || ''
  });

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    birthDate: user?.birthDate || ''
  });

  // Cập nhật profile và formData khi user thay đổi
  useEffect(() => {
    if (user) {
                    setProfile({
         name: user.name || '',
         email: user.email || '',
         phone: user.phone || '',
         avatar: user.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
         role: user.role || '',
         joinDate: user.createdAt || '',
         address: user.address || '',
         birthDate: user.birthDate || ''
       });

       setFormData({
         name: user.name || '',
         email: user.email || '',
         phone: user.phone || '',
         address: user.address || '',
         birthDate: user.birthDate || ''
       });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };



  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Tên không được để trống');
      return;
    }

    if (!formData.email.trim()) {
      toast.error('Email không được để trống');
      return;
    }

    setLoading(true);
    try {
             // Gọi API để cập nhật thông tin profile
       const response = await api.put('/auth/profile', {
         name: formData.name,
         email: formData.email,
         phone: formData.phone,
         address: formData.address,
         birthDate: formData.birthDate
       });

      if (response.data.success) {
        // Cập nhật thông tin trong AuthContext
        const updatedUser = { ...user, ...formData };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
                 // Cập nhật profile state
         setProfile(prev => ({
           ...prev,
           ...formData,
           avatar: previewUrl || prev.avatar
         }));
        
                 toast.success('Cập nhật thông tin thành công');
         setIsEditing(false);
         setSelectedFile(null);
         setPreviewUrl(null);
      } else {
        toast.error(response.data.message || 'Cập nhật thông tin thất bại');
      }
    } catch (error) {
      console.error('Update profile error:', error);
      
      if (error.response) {
        const { status, data } = error.response;
        
        switch (status) {
          case 400:
            toast.error(data?.message || 'Dữ liệu không hợp lệ');
            break;
          case 401:
            toast.error('Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại');
            break;
          case 403:
            toast.error('Không có quyền cập nhật thông tin');
            break;
          case 500:
            toast.error('Server đang gặp sự cố, vui lòng thử lại sau');
            break;
          default:
            toast.error(data?.message || 'Có lỗi xảy ra khi cập nhật thông tin');
        }
      } else if (error.request) {
        toast.error('Không thể kết nối đến server, vui lòng kiểm tra kết nối mạng');
      } else {
        toast.error('Có lỗi xảy ra khi cập nhật thông tin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: profile.name,
      email: profile.email,
      phone: profile.phone,
      address: profile.address,
      birthDate: profile.birthDate
    });
    setIsEditing(false);
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Thông tin của tôi</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Quản lý thông tin cá nhân và tài khoản</p>
        </div>
        <div className="flex items-center space-x-2">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center transition-colors"
            >
              <Edit className="w-4 h-4 mr-2" />
              Chỉnh sửa
            </button>
          ) : (
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              <button
                onClick={handleCancel}
                className="px-3 sm:px-4 py-2 text-sm sm:text-base text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center transition-colors"
              >
                <X className="w-4 h-4 mr-2" />
                Hủy
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center disabled:opacity-50 transition-colors"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Đang lưu...' : 'Lưu'}
              </button>
            </div>
          )}
        </div>
      </div>

             {/* Profile Card */}
       <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border">
                   {/* Avatar Section */}
          <div className="text-center mb-6">
            <div className="relative inline-block">
              <img
                src={previewUrl || profile.avatar}
                alt={profile.name}
                className="w-24 h-24 sm:w-32 sm:h-32 rounded-full mx-auto mb-4 object-cover border-4 border-gray-100 shadow-lg"
              />
              {isEditing && (
                <label className="absolute bottom-2 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700 transition-colors shadow-lg">
                  <Camera className="w-3 h-3 sm:w-4 sm:h-4" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              )}
            </div>
                         {/* Tên hiển thị dưới ảnh */}
             <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
               {isEditing ? formData.name : profile.name}
             </h2>
             
             {/* Ngày tham gia và vai trò trên cùng một hàng */}
             <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm text-gray-600">
               <div className="flex items-center space-x-2">
                 <Calendar className="w-4 h-4" />
                 <span>
                   {profile.joinDate ? new Date(profile.joinDate).toLocaleDateString('vi-VN') : 'Chưa có thông tin'}
                 </span>
               </div>
               <div className="flex items-center space-x-2">
                 <Shield className="w-4 h-4" />
                 <span>
                   {user?.role === 'admin' ? 'Quản trị viên' : 
                    user?.role === 'seller' ? 'Người bán' :
                    user?.role === 'designer' ? 'Thiết kế' : 
                    user?.role ? 'Người dùng' : 'Chưa có thông tin'}
                 </span>
               </div>
             </div>
           </div>

         <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Left Column - Basic Info */}
           <div className="space-y-4">
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-2">
                 Email
               </label>
               {isEditing ? (
                 <input
                   type="email"
                   name="email"
                   value={formData.email}
                   onChange={handleInputChange}
                   className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                 />
               ) : (
                 <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                   <Mail className="w-4 h-4 text-gray-400" />
                   <span className="text-gray-900">{profile.email || 'Chưa có thông tin'}</span>
                 </div>
               )}
             </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Số điện thoại
              </label>
              {isEditing ? (
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                                 <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                   <Phone className="w-4 h-4 text-gray-400" />
                   <span className="text-gray-900">{profile.phone || 'Chưa có thông tin'}</span>
                 </div>
              )}
            </div>

                       </div>

                     {/* Right Column - Additional Info */}
           <div className="space-y-4">
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-2">
                 Họ và tên
               </label>
               {isEditing ? (
                 <input
                   type="text"
                   name="name"
                   value={formData.name}
                   onChange={handleInputChange}
                   className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                 />
               ) : (
                 <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                   <User className="w-4 h-4 text-gray-400" />
                   <span className="text-gray-900">{profile.name || 'Chưa có thông tin'}</span>
                 </div>
               )}
             </div>

                         <div>
               <label className="block text-sm font-medium text-gray-700 mb-2">
                 Ngày sinh
               </label>
               {isEditing ? (
                 <input
                   type="date"
                   name="birthDate"
                   value={formData.birthDate}
                   onChange={handleInputChange}
                   className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                 />
               ) : (
                 <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                   <Calendar className="w-4 h-4 text-gray-400" />
                                      <span className="text-gray-900">
                      {profile.birthDate ? new Date(profile.birthDate).toLocaleDateString('vi-VN') : 'Chưa có thông tin'}
                    </span>
                 </div>
               )}
             </div>
           </div>
         </div>

         {/* Địa chỉ - Full width ở dưới cùng */}
         <div className="mt-6">
           <label className="block text-sm font-medium text-gray-700 mb-2">
             Địa chỉ
           </label>
           {isEditing ? (
             <textarea
               name="address"
               value={formData.address}
               onChange={handleInputChange}
               rows="3"
               className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
             />
           ) : (
             <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
               <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
               <span className="text-gray-900">{profile.address || 'Chưa có thông tin'}</span>
             </div>
           )}
         </div>
      </div>
    </div>
  );
};

export default AdminProfile; 